import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import { Button } from 'src/components/ui/button';
import BorderedInput from 'src/components/form-new/bordered-input';
import BorderedTextarea from 'src/components/form-new/bordered-textarea';
import BorderedSelect from 'src/components/form-new/bordered-select';
import { buildFeedbackTitle, capturePageContext, captureScreenshotFile } from 'src/components/feedback/capture-page-context';
import { useSubmitFeedbackMutation } from 'src/redux/feedback/feedback.endpoints';
import {
  FeedbackSubmissionError,
  FeedbackType,
  PageContext,
} from 'src/redux/feedback/feedback.types';
import { showNotification } from 'src/redux/notification/notification.slice';
import ErrorLogService from 'src/services/error-log/error-log.service';

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const FEEDBACK_TYPE_OPTIONS = [
  { value: 'Bug', label: 'Bug' },
  { value: 'Improvement', label: 'Improvement' },
  { value: 'Feature', label: 'Feature' },
];

const FeedbackModal: React.FC<FeedbackModalProps> = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const autoScreenshotPromiseRef = useRef<Promise<File | undefined> | null>(null);

  const [type, setType] = useState<FeedbackType>('Bug');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [screenshotFile, setScreenshotFile] = useState<File | null>(null);
  const [pageContext, setPageContext] = useState<PageContext | null>(null);
  const [validationErrors, setValidationErrors] = useState<{
    type?: string;
    title?: string;
    description?: string;
    pageContext?: string;
  }>({});

  const [submitFeedback, { isLoading }] = useSubmitFeedbackMutation();

  useEffect(() => {
    if (isOpen) {
      setPageContext(capturePageContext());
      setTitle((currentTitle) => currentTitle.trim() ? currentTitle : buildFeedbackTitle());
      autoScreenshotPromiseRef.current = captureScreenshotFile();
      return;
    }

    setType('Bug');
    setTitle('');
    setDescription('');
    setScreenshotFile(null);
    setPageContext(null);
    setValidationErrors({});
    autoScreenshotPromiseRef.current = null;

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [isOpen]);

  const validateForm = (): boolean => {
    const errors: {
      type?: string;
      title?: string;
      description?: string;
    } = {};

    if (!title.trim()) {
      errors.title = 'Title is required';
    }

    if (!description.trim()) {
      errors.description = 'Description is required';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const applyServerFieldErrors = (fieldErrors: FeedbackSubmissionError['fieldErrors']) => {
    if (!fieldErrors) {
      return;
    }

    setValidationErrors((currentErrors) => ({
      ...currentErrors,
      ...fieldErrors,
    }));
  };

  const handleScreenshotChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (!file) {
      setScreenshotFile(null);
      return;
    }

    if (!file.type.startsWith('image/')) {
      dispatch(showNotification({
        summary: 'Please upload a valid image file',
        sticky: false,
        theme: 'warning',
      }));
      event.target.value = '';
      setScreenshotFile(null);
      return;
    }

    setScreenshotFile(file);
  };

  const handleSubmit = async() => {
    if (!validateForm() || !pageContext) {
      return;
    }

    try {
      const autoScreenshot = screenshotFile ? undefined : await autoScreenshotPromiseRef.current ?? undefined;
      const response = await submitFeedback({
        type,
        title: title.trim(),
        description: description.trim(),
        pageContext,
        screenshot: screenshotFile ?? autoScreenshot,
      }).unwrap();

      dispatch(showNotification({
        summary: 'Feedback submitted successfully',
        sticky: false,
        theme: 'success',
      }));

      if (response.url) {
        window.open(response.url, '_blank', 'noopener,noreferrer');
      }

      onClose();
    } catch (error) {
      const submissionError = error as FeedbackSubmissionError;
      ErrorLogService.logError(error ?? 'Feedback submission error', 'feedback-modal', 'handleSubmit');

      applyServerFieldErrors(submissionError.fieldErrors);

      dispatch(showNotification({
        summary: submissionError.message ?? 'Failed to submit feedback. Please try again.',
        sticky: submissionError.isRetryable ?? false,
        theme: 'warning',
      }));
    }
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <DialogContent
        className="sm:max-w-[600px] bg-white"
        onClick={(event) => event.stopPropagation()}
      >
        <DialogHeader>
          <DialogTitle>
            Submit Feedback
          </DialogTitle>
          <DialogDescription>
            Share bugs, improvements, or feature requests with the team.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          <BorderedSelect
            label="Type"
            options={FEEDBACK_TYPE_OPTIONS}
            value={type}
            onChange={(event) => setType(event.target.value as FeedbackType)}
            error={validationErrors.type}
          />

          <BorderedInput
            label="Title"
            placeholder="Brief summary of your feedback"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            error={validationErrors.title}
          />

          <BorderedTextarea
            label="Description"
            placeholder="Describe the issue, improvement, or feature request in detail..."
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            rows={6}
            error={validationErrors.description}
          />

          <div className="space-y-2">
            <label
              htmlFor="feedback-screenshot-input"
              className="text-xs font-light text-gray-500"
            >
              Screenshot (optional)
            </label>
            <input
              id="feedback-screenshot-input"
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleScreenshotChange}
              className="block w-full text-sm text-gray-600 file:mr-4 file:rounded file:border-0 file:bg-gray-100 file:px-4 file:py-2 file:text-sm file:font-medium file:text-gray-700 hover:file:bg-gray-200"
            />
            {screenshotFile && (
              <p className="text-xs text-gray-500">
                Selected:
                {' '}
                {screenshotFile.name}
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={() => void handleSubmit()}
            disabled={isLoading}
          >
            {isLoading ? 'Submitting...' : 'Submit Feedback'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default FeedbackModal;
